import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";
import { mediaUrl } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { categoryLabel } from "@/lib/categories";
import { InquirySheet } from "@/components/InquirySheet";
import { DI, reviewsText } from "./desktopUi";
import { openCartDrawer } from "./CartDrawer";
import { useImageLook } from "@/lib/useImageFit";

/** Opens the quick view of a product from a card. */
export const openQuickView = (slug: string) => window.dispatchEvent(new CustomEvent("kms:quick-view", { detail: slug }));

/** Quick look at a product (photo, price, packs, notes) without leaving the list. */
export function QuickView() {
  const { t, locale } = useI18n();
  const { route, navigate } = useRouter();
  const { products } = useCatalog();
  const { addItem, isFavorite, toggleFavorite } = useCart();
  const [slug, setSlug] = useState<string | null>(null);
  const [img, setImg] = useState(0);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [inquiry, setInquiry] = useState(false);

  useEffect(() => {
    const show = (e: Event) => {
      setSlug((e as CustomEvent<string>).detail);
      setImg(0);
      setVariantId(null);
      setQty(1);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setSlug(null);
    window.addEventListener("kms:quick-view", show);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("kms:quick-view", show);
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  useEffect(() => setSlug(null), [route]);

  const p = slug ? products.find((x) => x.slug === slug) : undefined;
  if (!p) return null;
  const v = p.variants.find((x) => x.id === variantId) ?? p.variants[0];
  const underOrder = p.orderMode === "under_order";
  const images = p.images.length ? p.images : [""];
  const fav = isFavorite(p.id);
  const close = () => setSlug(null);

  return (
    <>
      {!inquiry && (
      <div className="qv-overlay" onMouseDown={close}>
        <div className="qv" role="dialog" aria-modal="true" aria-label={p.name} onMouseDown={(e) => e.stopPropagation()}>
          <button className="qv-x" onClick={close} aria-label={t("common.close")}>×</button>
          <div className="qv-gal">
            <QvPhoto src={images[img]} alt={p.name} />
            {images.length > 1 && (
              <div className="qv-thumbs">
                {images.slice(0, 5).map((src, i) => (
                  <QvThumb key={i} src={src} on={i === img} onClick={() => setImg(i)} />
                ))}
              </div>
            )}
          </div>
          <div className="qv-info">
            <div className="dk-eyebrow">{categoryLabel(p, t)}</div>
            <h2 className="dk-serif">{p.name}</h2>
            {(p.rating ?? 0) > 0 && <div className="qv-rating">{"★".repeat(Math.round(p.rating ?? 0))} <b>{(p.rating ?? 0).toFixed(1)}</b> <span>({reviewsText(p.reviewsCount ?? 0, locale, t)})</span></div>}
            <p className="qv-desc">{p.shortDescription}</p>
            {(p.flavor?.notes?.length ?? 0) > 0 && (
              <div className="qv-notes">{p.flavor!.notes!.map((n) => <span key={n}>{t(`flavor.notes.${n}`)}</span>)}</div>
            )}
            <div className="qv-price dk-serif">{v && v.priceUzs > 0 ? formatUzs(v.priceUzs, locale) : t("catalog.onRequest")}</div>
            {!underOrder && p.variants.length > 1 && (
              <div className="qv-variants">
                {p.variants.map((x) => (
                  <button key={x.id} data-active={x.id === v?.id} disabled={x.stock <= 0} onClick={() => setVariantId(x.id)}>{x.label}</button>
                ))}
              </div>
            )}
            <div className="qv-buy">
              {underOrder ? (
                <button className="dk-btn" onClick={() => setInquiry(true)}>{t("inquiry.title")}</button>
              ) : (
                <>
                  <span className="dco-qty">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="−">−</button>
                    <b>{qty}</b>
                    <button onClick={() => setQty((q) => Math.min(Math.max(1, v?.stock ?? 1), q + 1))} aria-label="+">+</button>
                  </span>
                  <button
                    className="dk-btn qv-add"
                    disabled={!v || v.stock <= 0}
                    onClick={() => {
                      if (!v) return;
                      addItem(p.id, v.id, qty);
                      close();
                      openCartDrawer();
                    }}
                  >
                    {DI.cart} {v && v.stock <= 0 ? t("product.outOfStock") : t("desktop.addToCart")}
                  </button>
                </>
              )}
              <button className="qv-fav" data-on={fav} onClick={() => toggleFavorite(p.id)} aria-label={t("profile.favorites")}>{DI.heart}</button>
            </div>
            <button className="qv-more" onClick={() => { close(); navigate({ name: "product", slug: p.slug }); }}>{t("desktop.qvMore")} {DI.arrow}</button>
          </div>
        </div>
      </div>
      )}
      {underOrder && (
        <InquirySheet
          product={p}
          open={inquiry}
          onClose={() => {
            setInquiry(false);
            close();
          }}
          mode="equipment"
        />
      )}
    </>
  );
}

/** Big photo of the quick view: the whole pack, on the photo's own background colour. */
function QvPhoto({ src, alt }: { src: string; alt: string }) {
  const url = mediaUrl(src, 800);
  const look = useImageLook(url);
  return (
    <div className="qv-main" data-fit={look.fit} style={look.bg ? { background: look.bg } : undefined}>
      {url && <img src={url} alt={alt} data-fit={look.fit} />}
    </div>
  );
}

function QvThumb({ src, on, onClick }: { src: string; on: boolean; onClick: () => void }) {
  const url = mediaUrl(src, 200);
  const look = useImageLook(url);
  return (
    <button data-on={on} onClick={onClick} style={look.bg ? { background: look.bg } : undefined}>
      {url && <img src={url} alt="" data-fit={look.fit} />}
    </button>
  );
}
